// Mostly copied directly from devour

import pluralize from 'pluralize';
import {
  forOwn as _forOwn,
  isArray as _isArray,
  isUndefined as _isUndefined,
  isPlainObject as _isPlainObject,
  includes as _includes,
  get as _get,
  map as _map,
  filter as _filter,
  find as _find,
  matches as _matches,
  flatten as _flatten,
} from 'lodash';

const cache = new (class {
  constructor() {
    this._cache = [];
  }

  set(type, id, deserialized) {
    this._cache.push({
      type,
      id,
      deserialized,
    });
  }

  get(type, id) {
    const match = _find(this._cache, (r) => r.type === type && r.id === id);
    return match && match.deserialized;
  }

  clear() {
    this._cache = [];
  }
})();

const attachHasOneFor = (jsonApi, model, attribute, item, included, key) => {
  if (!item.relationships) {
    return null;
  }

  const relatedItems = relatedItemsFor(model, attribute, item, included, key);

  if (relatedItems && relatedItems[0]) {
    return resource(jsonApi, relatedItems[0], included, true);
  } else {
    return null;
  }
};

const attachHasManyFor = (jsonApi, model, attribute, item, included, key) => {
  if (!item.relationships) {
    return null;
  }

  const relatedItems = relatedItemsFor(model, attribute, item, included, key);

  if (relatedItems && relatedItems.length > 0) {
    return collection(jsonApi, relatedItems, included, true);
  }

  return [];
};

const isRelationship = (attribute) => {
  return _isPlainObject(attribute) && _includes(['hasOne', 'hasMany'], attribute.jsonApi);
};

const isRelatedItemFor = (attribute, relatedItem, relationMapItem) => {
  let passesFilter = true;

  if (attribute.filter) {
    passesFilter = _matches(relatedItem.attributes, attribute.filter);
  }

  return (
    relatedItem.id === relationMapItem.id &&
    relatedItem.type === relationMapItem.type &&
    passesFilter
  );
};

// Returns unserialized related items.
const relatedItemsFor = (model, attribute, item, included, key) => {
  const relationMap = _get(item.relationships, [key, 'data'], false);

  if (!relationMap) {
    return [];
  }

  if (_isArray(relationMap)) {
    return _flatten(
      _map(relationMap, (relationMapItem) => {
        return _filter(included, (includedItem) => {
          return isRelatedItemFor(attribute, includedItem, relationMapItem);
        });
      }),
    );
  } else {
    return _filter(included, (includedItem) => {
      return isRelatedItemFor(attribute, includedItem, relationMap);
    });
  }
};

const attachRelationsFor = (jsonApi, model, attribute, item, included, key) => {
  let relation = null;

  if (attribute.jsonApi === 'hasOne') {
    relation = attachHasOneFor(jsonApi, model, attribute, item, included, key);
  }

  if (attribute.jsonApi === 'hasMany') {
    relation = attachHasManyFor(jsonApi, model, attribute, item, included, key);
  }

  return relation;
};

const collection = (jsonApi, items, included, useCache = false) => {
  return items.map((item) => {
    return resource(jsonApi, item, included, useCache);
  });
};

// This is the actual deserialization function
const resource = (jsonApi, item, included, useCache = false) => {
  // TODO: debug caching when needing relationships of cached items
  if (useCache) {
    const cachedItem = cache.get(item.type, item.id);
    if (cachedItem) return cachedItem;
  }

  const model = jsonApi.modelFor(pluralize.singular(item.type));

  const deserializedModel = {
    id: item.id,
    type: item.type,
  };

  _forOwn(item.attributes, (value, key) => {
    let attributeConfig = model.attributes[key];

    if (_isUndefined(attributeConfig) && key !== 'id') {
      key = key.replace(/-([a-z])/g, (g) => {
        return g[1].toUpperCase();
      });
      attributeConfig = model.attributes[key];
    }

    if (!_isUndefined(attributeConfig) && key !== 'id') {
      deserializedModel[key] = value;
    }
  });

  _forOwn(model.formatters, (formatter, key) => {
    deserializedModel[key] = formatter(deserializedModel);
  });

  cache.set(item.type, item.id, deserializedModel);

  _forOwn(item.relationships, (value, rel) => {
    let relConfig = model.attributes[rel];
    const key = rel;

    if (_isUndefined(relConfig)) {
      rel = rel.replace(/-([a-z])/g, (g) => {
        return g[1].toUpperCase();
      });
      relConfig = model.attributes[rel];
    }

    if (!_isUndefined(relConfig) && isRelationship(relConfig)) {
      deserializedModel[rel] = attachRelationsFor(jsonApi, model, relConfig, item, included, key);
    }
  });

  const params = ['meta', 'links'];
  params.forEach((param) => {
    if (item[param]) {
      deserializedModel[param] = item[param];
    }
  });
  return deserializedModel;
};

export default {
  collection,
  resource,
  cache,
};
